import React, { Suspense, useEffect, useMemo } from "react";
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router";

import { AuthenticatedRoute } from "./AuthenticationRoute";
import { AppLoadTracker } from "Core/Telemetry/AppLoadTracker";
import { ErrorBoundary } from "Components/Pages/ErrorBoundary";
import { FluentProvider } from "@fluentui/react-components";
import { LoadingPage } from "Components/Pages/LoadingPage";
import { LoginPage } from "../Components/Pages/LoginPage";
import { M365PulseSiteLayout } from "./M365PulseSiteLayout";
import { Paths } from "Shared/Utils";
import { StartPage } from "Components/Pages/StartPage";
import { TeamAppModel } from "Apps/TeamAppModel";
import { markAppNavigationStart } from "Core/Telemetry/CustomerMetrics";
import { observer } from "mobx-react";
import { preloadAppSettings, useGlobalSetting } from "Store/GlobalSettingStore";
import { useRootStore } from "Store/RootStore";

const M365PulseAppBase = () => {
    const appStore = useRootStore("AppSettingStore");
    const user = useRootStore("MyStore");
    const themeStore = useGlobalSetting("theme");

    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e: MediaQueryListEvent) => {
            themeStore.setSystemPrefersDark(e.matches);
        };
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, [themeStore]);

    const enhancedAppModel = useMemo(
        () =>
            appStore.allApps.map((app: TeamAppModel) => ({
                ...app,
                loader: () => {
                    preloadAppSettings(app.id);
                    if (appStore.currentApp.id === app.id) {
                        return null;
                    }
                    markAppNavigationStart(app.id);
                    appStore.setCurrentApp(app);
                    return null;
                }
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [appStore, user.flighting, user.profile.Roles]
    );

    const router = useMemo(
        () =>
            createBrowserRouter(
                createRoutesFromElements(
                    <>
                        <Route path="/login" element={<LoginPage />} />
                        <Route
                            path="/"
                            element={
                                <AuthenticatedRoute>
                                    <M365PulseSiteLayout />
                                </AuthenticatedRoute>
                            }
                            errorElement={<ErrorBoundary />}
                            hydrateFallbackElement={<LoadingPage />}
                        >
                            <Route index element={<StartPage />} />
                            {enhancedAppModel.map((app, key) => {
                                const appExists = appStore.appModels.find((a) => a.id === app.id);
                                const appLocal = appExists ? (
                                    <AppLoadTracker appId={app.id}>
                                        <app.app />
                                    </AppLoadTracker>
                                ) : null;
                                return <Route key={key} path={`${app.path}/*`} element={appLocal} loader={app.loader} />;
                            })}
                        </Route>
                    </>
                ),
                {
                    basename: Paths.getBaseUri()
                }
            ),
        [appStore.appModels, enhancedAppModel]
    );

    return (
        <FluentProvider theme={themeStore.currentFluentTheme}>
            <Suspense fallback={<LoadingPage />}>
                <RouterProvider router={router} />
            </Suspense>
        </FluentProvider>
    );
};

export const M365PulseApp = observer(M365PulseAppBase);
